/* SellerCatalogImport — main hub for the multi-source AI catalog importer.
 *
 * Layout:
 *   Top:        "Bring your catalog in" hero + quick stats
 *   Tile grid:  6 source options (CSV/Excel, PDF/image, Shopify, Google
 *               Sheet, Email-in, Webhook). Clicking opens the relevant flow.
 *   Profiles:   Saved import profiles list (re-run with one click)
 *   Settings:   Field visibility wizard
 *   History:    Recent import jobs
 *
 * Routes here:
 *   GET  /seller/catalog/import       → this page
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  FileSpreadsheet, FileText, Image as ImageIcon, ShoppingBag,
  Cloud, AtSign, Globe, Upload, Sparkles, Eye, RefreshCcw,
  CheckCircle2, AlertCircle, Loader2, Plus, Trash2, Settings2, X,
  ArrowRight, Database, Clock, BookOpen, Pencil, FolderArchive,
} from "lucide-react";
import ImportPreviewModal from "../../components/catalog-import/ImportPreviewModal";
import FieldVisibilityWizard from "../../components/catalog-import/FieldVisibilityWizard";
import ConnectorSetupModal from "../../components/catalog-import/ConnectorSetupModal";
import MagicLinkModal from "../../components/catalog-import/MagicLinkModal";
import BulkEditModal from "../../components/catalog-import/BulkEditModal";
import CatalogNeedsAttention from "../../components/catalog-import/CatalogNeedsAttention";
import BulkImageUploadModal from "../../components/catalog-import/BulkImageUploadModal";
import ImportStatusOverlay from "../../components/catalog-import/ImportStatusOverlay";

/* Turn a raw backend error string into a clear reason + actionable steps so the
   seller knows exactly what to fix and how to get the file imported. */
function parseImportError(raw) {
  const msg = (raw || "").toString();
  const low = msg.toLowerCase();
  if (low.includes("empty") || low.includes("no rows") || low.includes("no product rows") || low.includes("product rows") || low.includes("rows found")) {
    return { title: "That file looks empty", detail: "We opened the file but couldn't find any product rows.",
      suggestions: [
        "Make sure the sheet has a header row plus at least one product row underneath it.",
        "Remove any blank rows above your column headers.",
        "If you exported from another tool, confirm the data is on the first sheet/tab and re-export.",
      ] };
  }
  if (low.includes("not a valid") || low.includes("not valid") || low.includes("valid spreadsheet") || low.includes("recognized inventory") || low.includes("recognised inventory")) {
    return { title: "We couldn't read this file", detail: "The file didn't look like a spreadsheet or recognised linesheet we can parse.",
      suggestions: [
        "Upload a CSV, Excel (.xlsx/.xls), PDF or image linesheet.",
        "If it's a spreadsheet, re-save it as a fresh .csv or .xlsx and try again.",
        "Make sure the file isn't corrupted or password-protected.",
      ] };
  }
  if (low.includes("unsupported") || low.includes("file type") || low.includes("415")) {
    return { title: "That file type isn't supported", detail: "We can read CSV, Excel (.xlsx/.xls), PDF and image linesheets.",
      suggestions: [
        "Save your spreadsheet as .csv or .xlsx and upload again.",
        "For Google Sheets: File → Download → CSV first (or use the Google Sheet connector).",
        "Avoid password-protected, .numbers or .pages files.",
      ] };
  }
  if (low.includes("too large") || low.includes("413")) {
    return { title: "That file is too large", detail: msg || "The file exceeds the upload size limit.",
      suggestions: [
        "Split the catalog into smaller batches and upload them one at a time.",
        "Remove unused columns or extra sheets to shrink the file.",
      ] };
  }
  if (low.includes("column") || low.includes("map") || low.includes("sku") || low.includes("name") || low.includes("price") || low.includes("parse")) {
    return { title: "We couldn't read your columns", detail: "To import, each product needs at least a name, a SKU and a wholesale price.",
      suggestions: [
        "Keep your column headers in the very first row of the sheet.",
        "Name the key columns clearly, e.g. 'Product Name', 'SKU' and 'Wholesale Price'.",
        "Delete any merged cells, summary rows or notes above the table.",
        "If your headers are unusual, you can still map them by hand on the preview screen.",
      ] };
  }
  return { title: "We couldn't import this file", detail: msg || "Something went wrong while reading your file.",
    suggestions: [
      "Confirm the file opens correctly on your computer and isn't corrupted.",
      "Make sure the first row contains your column headers (Name, SKU, Price…).",
      "Try saving a fresh copy as .csv or .xlsx and upload again.",
      "Still stuck? Try the 'Magic Link' option or contact support.",
    ] };
}

const SOURCE_TILES = [
  { kind: "magic",   label: "Magic Link",       icon: Sparkles,
    desc: "Paste any public file URL (Google Drive, Dropbox, Sheets, direct CSV/Excel/PDF).",
    tier: "Quick import", primary: true },
  { kind: "csv",     label: "CSV / Excel",      icon: FileSpreadsheet,
    desc: "Drop your inventory spreadsheet — AI maps the columns automatically.",
    tier: "Quick import", primary: true },
  { kind: "pdf",     label: "PDF / image linesheet", icon: FileText,
    desc: "Got a PDF linesheet or a photo of your stock list? AI extracts every row.",
    tier: "Quick import" },
  { kind: "shopify", label: "Shopify",          icon: ShoppingBag,
    desc: "1-click import + daily auto-sync of products, prices and stock.",
    tier: "Auto-sync", connector: true },
  { kind: "gsheet",  label: "Google Sheet",     icon: Cloud,
    desc: "Live sync from a Google Sheet — update once, your catalog follows.",
    tier: "Auto-sync", connector: true },
  { kind: "email",   label: "Email-in",         icon: AtSign,
    desc: "Forward your daily inventory file to your private verqo address.",
    tier: "Auto-sync", connector: true },
  { kind: "webhook", label: "Real-time webhook", icon: Globe,
    desc: "POST JSON from your ERP — verqo updates the catalog instantly.",
    tier: "Auto-sync", connector: true },
];

export default function SellerCatalogImport() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [extracting, setExtracting] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const [importError, setImportError] = useState(null); // {title, detail, suggestions, errors}
  const [preview, setPreview] = useState(null); // {job_id, detected_columns, detected_mappings, preview_rows}
  const [profiles, setProfiles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [vizOpen, setVizOpen] = useState(false);
  const [connectorOpen, setConnectorOpen] = useState(null); // kind string when open
  const [magicOpen, setMagicOpen] = useState(false);
  const [bulkEditJob, setBulkEditJob] = useState(null); // {id, label} when open
  const [bulkImagesOpen, setBulkImagesOpen] = useState(false);
  const [inboundEmail, setInboundEmail] = useState("");
  const [connectors, setConnectors] = useState([]);

  const refresh = async () => {
    try {
      const [j, p, c] = await Promise.all([
        api.get("/seller/catalog-import/jobs"),
        api.get("/seller/catalog-import/profiles"),
        api.get("/seller/catalog-import/connectors"),
      ]);
      setJobs(j.data?.jobs || []);
      setProfiles(p.data?.profiles || []);
      setConnectors(c.data?.connectors || []);
      setInboundEmail(c.data?.inbound_email || "");
    } catch (e) {
      // First-time visitors won't have the collections yet — safe to ignore
    }
  };
  useEffect(() => { refresh(); }, []);

  const onFile = async (e, profileId) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = ""; // allow re-upload of same file
    setImportError(null);
    setUploadName(f.name);
    setExtracting(true);
    const fd = new FormData();
    fd.append("file", f);
    if (profileId) fd.append("profile_id", profileId);
    try {
      const r = await api.post("/seller/catalog-import/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (err) {
      setImportError(parseImportError(err.response?.data?.detail));
    } finally {
      setExtracting(false);
    }
  };

  const onApply = async ({ jobId, mappings, rows, saveAsProfile, profileName, autoApply }) => {
    try {
      const r = await api.post("/seller/catalog-import/apply", {
        job_id: jobId, mappings, rows,
        save_as_profile: saveAsProfile, profile_name: profileName, auto_apply: autoApply,
      });
      const created = r.data.created || 0;
      const updated = r.data.updated || 0;
      const skipped = r.data.skipped || 0;
      // Nothing landed in the catalog — explain why + how to fix instead of a
      // misleading "success" toast.
      if (created + updated === 0) {
        setPreview(null);
        setImportError({
          title: "No products were imported",
          detail: `${skipped} row${skipped === 1 ? "" : "s"} were skipped because they were missing a required field. Every product needs a name, a SKU and a wholesale price.`,
          suggestions: [
            "Open your file and fill in any blank Name, SKU or Wholesale Price cells.",
            "On the preview screen, make sure those three columns are mapped correctly.",
            "Remove summary/total rows that don't represent a real product.",
            "Save the corrected file and upload it again.",
          ],
          errors: r.data.errors,
        });
        refresh();
        return;
      }
      toast.success(
        (t) => (
          <div className="flex items-center gap-3" data-testid="apply-success-toast">
            <CheckCircle2 className="w-4 h-4 text-emerald-600"/>
            <div className="flex-1">
              <div className="text-sm font-medium">Catalog updated</div>
              <div className="text-[11px] text-foreground/55">
                {created} new · {updated} updated · {skipped} skipped
              </div>
            </div>
            <button onClick={() => { toast.dismiss(t); nav("/seller/catalog?after_import=1"); }}
              className="text-[12px] underline text-foreground/80 hover:text-foreground">Review &amp; edit →</button>
          </div>
        ),
        { duration: 8000 }
      );
      setPreview(null);
      refresh();
    } catch (e) {
      setPreview(null);
      setImportError(parseImportError(e.response?.data?.detail || "Apply failed"));
    }
  };

  const deleteProfile = async (pid) => {
    if (!window.confirm("Delete this saved profile?")) return;
    try { await api.delete(`/seller/catalog-import/profiles/${pid}`); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  const reimportWithProfile = (pid) => {
    fileRef.current.dataset.profile = pid;
    fileRef.current.click();
  };

  return (
    <>
      <div className="border-b border-border bg-background sticky top-0 z-10 px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between" data-testid="catalog-import-page">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Apps · Catalog · Import</div>
          <h1 className="display text-2xl mt-1 tracking-tight">Bring your catalog in</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setVizOpen(true)} className="rounded-full h-10 px-4">
            <Settings2 className="w-4 h-4 mr-1.5"/>Field visibility
          </Button>
          <Button onClick={() => nav("/seller/catalog")} variant="outline" className="rounded-full h-10 px-4">
            View catalog <ArrowRight className="w-3 h-3 ml-1"/>
          </Button>
        </div>
      </div>

      <div className="page-shell space-y-10">
        {/* Needs-attention inline panel — shows after import IF there are
            critical gaps. Dismissable. Click any gap → BulkFixModal. */}
        <CatalogNeedsAttention mode="panel"/>

        {/* Hidden file input — drives both first-time and saved-profile uploads */}
        <input ref={fileRef} type="file" accept=".csv,.tsv,.xls,.xlsx,.pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.docx,.txt"
          className="hidden" data-testid="hidden-file"
          onChange={(e) => {
            const pid = fileRef.current.dataset.profile || null;
            fileRef.current.dataset.profile = "";
            onFile(e, pid);
          }}/>

        {/* SOURCE TILES */}
        <section>
          <h2 className="text-sm mono uppercase tracking-[0.18em] text-foreground/55">Pick a source</h2>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SOURCE_TILES.map(t => {
              const enabled = !!connectors.find(c => c.kind === t.kind && c.enabled);
              return (
                <button key={t.kind}
                  onClick={() => {
                    if (t.kind === "magic") {
                      setMagicOpen(true);
                    } else if (t.kind === "csv" || t.kind === "pdf") {
                      fileRef.current.dataset.profile = "";
                      fileRef.current.click();
                    } else {
                      setConnectorOpen(t.kind);
                    }
                  }}
                  data-testid={`tile-${t.kind}`}
                  className={`text-left rounded-2xl border bg-white p-5 hover:border-foreground/40 transition-all relative ${t.primary ? "border-foreground/30" : "border-border"}`}>
                  <div className="flex items-start justify-between">
                    <span className="w-10 h-10 rounded-lg bg-foreground text-background grid place-items-center"><t.icon className="w-5 h-5"/></span>
                    <div className="flex gap-1">
                      {enabled && <span className="chip bg-emerald-50 border border-emerald-200 text-emerald-700 text-[9px]">Connected</span>}
                      <span className="chip bg-secondary text-[9px] mono uppercase tracking-wider">{t.tier}</span>
                    </div>
                  </div>
                  <div className="display text-lg mt-4">{t.label}</div>
                  <div className="text-xs text-foreground/60 mt-1 leading-relaxed">{t.desc}</div>
                  <div className="mt-4 inline-flex items-center gap-1 text-[12px] text-foreground/70 group-hover:gap-2 transition-all">
                    {t.kind === "magic" ? "Paste link" : t.connector ? "Set up" : "Upload file"} <ArrowRight className="w-3 h-3"/>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* BULK IMAGE UPLOAD — drop a ZIP of product images matched by SKU */}
        <section data-testid="bulk-image-upload-section">
          <div className="rounded-2xl border border-border bg-gradient-to-br from-secondary/40 to-white p-5 flex items-start gap-4">
            <span className="w-11 h-11 rounded-xl bg-foreground text-background grid place-items-center shrink-0">
              <FolderArchive className="w-5 h-5"/>
            </span>
            <div className="flex-1 min-w-0">
              <div className="display text-base">Bulk image upload</div>
              <div className="text-[12px] text-foreground/65 mt-0.5 leading-relaxed">
                Drop a ZIP of images named by SKU (e.g. <span className="mono">BRD-001.jpg</span>, <span className="mono">BRD-001-2.jpg</span>). Auto-attaches primary + gallery (up to 5).
              </div>
            </div>
            <Button onClick={() => setBulkImagesOpen(true)} data-testid="bulk-images-open"
              className="rounded-full bg-foreground text-background hover:bg-foreground/90 shrink-0">
              <Upload className="w-3.5 h-3.5 mr-1.5"/>Upload ZIP
            </Button>
          </div>
        </section>

        {/* SAVED PROFILES */}
        {profiles.length > 0 && (
          <section data-testid="saved-profiles">
            <div className="flex items-center justify-between">
              <h2 className="text-sm mono uppercase tracking-[0.18em] text-foreground/55">Saved import profiles</h2>
              <div className="text-[11px] text-foreground/55">Drop new files to re-run any of these. Daily refresh = $0 AI cost.</div>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {profiles.map(p => (
                <div key={p.id} className="rounded-2xl border border-border bg-white p-5 flex flex-col" data-testid={`profile-${p.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="w-9 h-9 rounded-lg bg-secondary border border-border grid place-items-center"><BookOpen className="w-4 h-4 text-foreground/60"/></div>
                    <button onClick={() => deleteProfile(p.id)} className="text-foreground/40 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5"/></button>
                  </div>
                  <div className="font-medium text-sm mt-3">{p.name}</div>
                  <div className="text-[11px] text-foreground/55 mt-1 mono uppercase tracking-wider">
                    {p.source_kind} · {p.mappings?.length || 0} fields · {p.use_count || 0} uses
                  </div>
                  {p.auto_apply && (
                    <div className="text-[11px] text-emerald-700 mt-1 inline-flex items-center gap-1">
                      <Sparkles className="w-3 h-3"/>Auto-applies on inbound matches
                    </div>
                  )}
                  <Button size="sm" onClick={() => reimportWithProfile(p.id)} data-testid={`reimport-${p.id}`}
                    className="mt-4 rounded-full bg-foreground text-background hover:bg-foreground/90 h-9 text-xs">
                    <Upload className="w-3.5 h-3.5 mr-1.5"/>Use this profile
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CONNECTORS — only shown if any exist */}
        {connectors.length > 0 && (
          <section>
            <h2 className="text-sm mono uppercase tracking-[0.18em] text-foreground/55">Connected sources</h2>
            <div className="mt-4 space-y-2">
              {connectors.map(c => (
                <div key={c.id} className="rounded-xl border border-border bg-white p-4 flex items-center gap-4">
                  <span className={`w-9 h-9 rounded-lg grid place-items-center ${c.enabled ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-secondary text-foreground/45 border border-border"}`}>
                    <Database className="w-4 h-4"/>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{c.name}</div>
                    <div className="text-[11px] text-foreground/55 mt-0.5 mono uppercase tracking-wider">
                      {c.kind} · {c.schedule} · last run {c.last_run_at ? new Date(c.last_run_at).toLocaleString() : "never"}
                      {c.last_status && c.last_status !== "ok" && <span className="ml-2 text-rose-600">({c.last_status})</span>}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setConnectorOpen(c.kind)} className="rounded-full h-8 px-3 text-xs">Edit</Button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* JOB HISTORY */}
        <section data-testid="job-history">
          <div className="flex items-center justify-between">
            <h2 className="text-sm mono uppercase tracking-[0.18em] text-foreground/55">Recent imports</h2>
            <button onClick={refresh} className="text-[11px] text-foreground/55 hover:text-foreground inline-flex items-center gap-1"><RefreshCcw className="w-3 h-3"/>Refresh</button>
          </div>
          <div className="mt-4 rounded-2xl border border-border bg-white overflow-hidden">
            {jobs.length === 0 ? (
              <div className="p-12 text-center text-foreground/45">
                <Clock className="w-7 h-7 mx-auto opacity-50"/>
                <div className="mt-3 text-sm">No imports yet. Drop a file above to begin.</div>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-secondary/40 border-b border-border">
                  <tr className="text-foreground/55">
                    <Th>When</Th><Th>Source</Th><Th>File / Profile</Th><Th right>Total</Th><Th right>New</Th><Th right>Updated</Th><Th right>Skipped</Th><Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((j, i) => {
                    const canEdit = j.status === "applied" && j.rows_created + j.rows_updated > 0;
                    return (
                      <tr key={j.id}
                        onClick={() => canEdit && setBulkEditJob({ id: j.id, label: j.filename || j.profile_name || "this import" })}
                        className={`border-b last:border-b-0 border-border hover:bg-secondary/40 ${canEdit ? "cursor-pointer" : ""}`}
                        data-testid={`job-row-${j.id}`}
                        title={canEdit ? "Click to bulk-edit the products from this import" : ""}>
                        <Td><span className="mono text-[11px] text-foreground/65">{new Date(j.created_at).toLocaleString()}</span></Td>
                        <Td><span className="chip bg-secondary text-[10px] uppercase mono">{j.source_kind}</span></Td>
                        <Td><div className="truncate max-w-xs">{j.filename || j.profile_name || "—"}</div></Td>
                        <Td right><span className="mono">{j.rows_total}</span></Td>
                        <Td right><span className="mono text-emerald-700">{j.rows_created}</span></Td>
                        <Td right><span className="mono text-blue-700">{j.rows_updated}</span></Td>
                        <Td right><span className="mono text-foreground/55">{j.rows_skipped}</span></Td>
                        <Td>
                          <div className="inline-flex items-center gap-2">
                            <StatusChip status={j.status}/>
                            {canEdit && <span className="text-[10px] text-foreground/45 inline-flex items-center gap-0.5"><Pencil className="w-2.5 h-2.5"/>edit</span>}
                          </div>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Inbound-email hint */}
        {inboundEmail && (
          <section className="rounded-2xl bg-gradient-to-br from-foreground to-foreground/90 text-background p-7">
            <div className="text-[10px] mono uppercase tracking-[0.22em] opacity-65">Magic move</div>
            <div className="display text-xl mt-2">Your private inventory inbox</div>
            <p className="text-sm opacity-80 mt-1.5 max-w-xl leading-relaxed">
              Forward your daily inventory file to <strong className="opacity-100">{inboundEmail}</strong> and verqo
              will read it and update your catalog — using whichever saved profile matches. Zero clicks.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 text-[12px] mono">
              {inboundEmail}
              <button onClick={() => { navigator.clipboard.writeText(inboundEmail); toast.success("Copied"); }}
                className="text-accent hover:underline">copy</button>
            </div>
          </section>
        )}
      </div>

      {/* Dialogs */}
      {preview && (
        <ImportPreviewModal
          preview={preview}
          onClose={() => setPreview(null)}
          onApply={onApply}
        />
      )}
      {vizOpen && (
        <FieldVisibilityWizard onClose={() => setVizOpen(false)} />
      )}
      {connectorOpen && (
        <ConnectorSetupModal
          kind={connectorOpen}
          inboundEmail={inboundEmail}
          profiles={profiles}
          existingConnector={connectors.find(c => c.kind === connectorOpen)}
          onClose={() => setConnectorOpen(null)}
          onSaved={() => { setConnectorOpen(null); refresh(); }}
        />
      )}
      {magicOpen && (
        <MagicLinkModal
          profiles={profiles}
          onClose={() => setMagicOpen(false)}
          onPreview={(p) => { setMagicOpen(false); setPreview(p); }}
        />
      )}
      {bulkEditJob && (
        <BulkEditModal
          jobId={bulkEditJob.id}
          jobLabel={bulkEditJob.label}
          onClose={() => setBulkEditJob(null)}
          onSaved={refresh}
          onOpenVisibility={() => { setBulkEditJob(null); setVizOpen(true); }}
        />
      )}
      {bulkImagesOpen && (
        <BulkImageUploadModal onClose={() => setBulkImagesOpen(false)}/>
      )}
      <ImportStatusOverlay
        loading={extracting}
        filename={uploadName}
        error={importError}
        onClose={() => setImportError(null)}
        onRetry={() => { setImportError(null); fileRef.current?.click(); }}
      />
    </>
  );
}

function Th({ children, right }) {
  return <th style={{ textAlign: right ? "right" : "left" }} className="text-[10px] uppercase tracking-[0.18em] mono font-medium py-2.5 px-3">{children}</th>;
}
function Td({ children, right }) {
  return <td style={{ textAlign: right ? "right" : "left", verticalAlign: "middle" }} className="py-2.5 px-3">{children}</td>;
}
function StatusChip({ status }) {
  const map = {
    applied:   { l: "Applied",  cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
    preview:   { l: "Preview",  cls: "bg-amber-50 text-amber-700 border-amber-200",       icon: Eye },
    extracting:{ l: "Working",  cls: "bg-blue-50 text-blue-700 border-blue-200",          icon: Loader2 },
    failed:    { l: "Failed",   cls: "bg-rose-50 text-rose-700 border-rose-200",          icon: AlertCircle },
    discarded: { l: "Discarded",cls: "bg-secondary text-foreground/55 border-border",     icon: X },
    pending:   { l: "Pending",  cls: "bg-secondary text-foreground/55 border-border",     icon: Clock },
  };
  const m = map[status] || { l: status, cls: "bg-secondary border-border", icon: Clock };
  return (
    <span className={`chip border text-[10px] ${m.cls}`}>
      <m.icon className="w-2.5 h-2.5"/>{m.l}
    </span>
  );
}
